import { useQuery } from '@tanstack/react-query';

import { FERRY_ADMIN_URL } from 'Constants';
import { fetchEntityList } from 'Modules/Crud/EntityApi';

/** Only the two fields the filter needs — the endpoint returns whole ferries. */
export interface ScheduleFerry {
	id: string;
	name: string;
}

/** Stable identity, so a cache miss doesn't hand back a new array on every render. */
const NO_FERRIES: ScheduleFerry[] = [];

/**
 * The vessels the staff schedule can filter by.
 *
 * Fetched rather than derived from the trips on screen: the drawer offers every vessel the operator
 * has, the same way the route list offers every route, so what's on offer doesn't change as the
 * week is paged. (The ferry schedule's "View options" modal does derive its list from the loaded
 * trips — a different question, "which of these am I looking at", asked of one week.)
 *
 * Keyed and paged the same as every other all-ferries read in the app, so the drawer and the
 * capacity restriction form share one cache entry rather than each fetching the list. 500 is the
 * page size that read uses; an operator with more vessels than that is not a case this app has.
 *
 * Errors are swallowed to an empty list, which the list component shows as "No ferries to filter."
 * A filter that can't offer its options is a section that can't be used, not a page that failed.
 */
export function useScheduleFerries(enabled = true): ScheduleFerry[] {
	const { data } = useQuery({
		queryKey: ['ferries', 'list'],
		queryFn: () => fetchEntityList<ScheduleFerry>(FERRY_ADMIN_URL, 1, 500),
		staleTime: 5 * 60 * 1000,
		// The list comes from `/api/admin`, which is staff-only. A customer reading the charters table
		// on /staff-bookings would fire a request that can only 403 — swallowed to an empty list, so
		// harmless, but a request nobody can answer is still one worth not making.
		enabled,
	});

	return data?.items ?? NO_FERRIES;
}
